import React from "react";
import { Box, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow, Divider, Button, Link } from "@mui/material";
import { Download } from "@mui/icons-material";
import { getRequest } from "Includes/api/apicall";
import { GET_URL } from "Includes/urls";
import {
  normalizeServices,
  erpAmountBeforeGst,
  erpTotalInclGst,
  idCardAmountTotal,
  servicesTotalInclGst,
  digitalPricesTotal,
  singleDigitalFeatureAmount,
} from "Containers/Agreement/agreementFinance";

const fmtMoney = (v) => {
  const n = Number(v || 0);
  if (Number.isNaN(n)) return "₹0.00";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const Section = ({ title, children }) => (
  <Box mb={2}>
    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Box p={2} border={1} borderColor="grey.300" borderRadius={1} bgcolor="#fafafa">
      {children}
    </Box>
  </Box>
);

const LabelValue = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="body2">{value ?? "-"}</Typography>
  </Box>
);

const parseDigitalRows = (raw) => {
  if (!raw || typeof raw !== "object") return [];
  return Object.entries(raw).map(([id, v]) => {
    let price = "";
    let renewal = "";
    if (Array.isArray(v)) {
      price = v[0];
      renewal = v[1];
    } else if (v && typeof v === "object") {
      price = v.price;
      renewal = v.renewal_price;
    }
    return { id: String(id), price, renewal };
  });
};

export default function AgreementSummary({ data, requestProps }) {
  const d = data || {};
  const [erpCatalog, setErpCatalog] = React.useState([]);
  const [digitalCatalog, setDigitalCatalog] = React.useState([]);

  React.useEffect(() => {
    const props = requestProps || {};
    getRequest(GET_URL.erpfeature.api, { limit: 500, pageno: 1 }, props)
      .then((res) => {
        if (res?.status === 200) {
          setErpCatalog(res.data?.data || []);
        }
      })
      .catch(() => {});
    getRequest(GET_URL.digitalmarketingfeature.api, { limit: 500, pageno: 1 }, props)
      .then((res) => {
        if (res?.status === 200) {
          setDigitalCatalog(res.data?.data || []);
        }
      })
      .catch(() => {});
  }, [requestProps]);

  const erpIdToName = React.useMemo(() => {
    const m = {};
    (erpCatalog || []).forEach((f) => {
      m[String(f.id)] = f.name || `ID ${f.id}`;
    });
    return m;
  }, [erpCatalog]);

  const digitalById = React.useMemo(() => {
    const m = {};
    (digitalCatalog || []).forEach((f) => {
      m[String(f.id)] = f;
    });
    return m;
  }, [digitalCatalog]);

  const erpBase = erpAmountBeforeGst(d);
  const gstPct = safeNumber(d.gst);
  const erpGst = erpBase * (gstPct / 100);
  const erpTotal = erpTotalInclGst(d);
  const students = safeNumber(d.numberOfStudents);
  const perStudent = safeNumber(d.peramount);
  const perId = safeNumber(d.perId);
  const idCardTotal = idCardAmountTotal(d);

  const services = normalizeServices(d.services);
  const serviceRows = Object.entries(services).map(([name, meta]) => {
    const amount = safeNumber(meta?.Amount);
    const sgst = safeNumber(meta?.GST);
    const gstAmt = sgst ? amount * (sgst / 100) : 0;
    return {
      name,
      amount,
      gstPct: sgst,
      gstAmt,
      total: amount + gstAmt,
      description: meta?.Description || "",
    };
  });
  const servicesTotal = servicesTotalInclGst(services);
  const digitalTotal = digitalPricesTotal(d.digitalfeature, d);
  const grandTotal = erpTotal + idCardTotal + servicesTotal + digitalTotal;

  const paymentTerms = Array.isArray(d.paymentTerms) ? d.paymentTerms : [];
  const paymentEditBy = String(d.payment_terms_edit_by || "percentage");
  
  const erpFeatures = Array.isArray(d.erpfeature) ? d.erpfeature : [];
  const erpFeatureLabels = erpFeatures.map((item) => {
    const s = String(item);
    if (/^\d+$/.test(s)) return erpIdToName[s] || `ERP #${s}`;
    return s;
  });

  const digitalRows = parseDigitalRows(d.digitalfeature);
  const digitalRowsWithMeta = digitalRows.map((row) => {
    const meta = digitalById[row.id];
    const label = meta?.name || `Other Service #${row.id}`;
    return { ...row, label };
  });
  const digitalTermsBlocks = [];
  digitalRows.forEach((row) => {
    const meta = digitalById[row.id];
    const name = meta?.name || `Other Service #${row.id}`;
    const terms = Array.isArray(meta?.terms_and_condition)
      ? meta.terms_and_condition.filter((t) => t != null && String(t).trim() !== "")
      : [];
    if (terms.length) digitalTermsBlocks.push({ name, terms });
  });

  const showErp = erpTotal > 0 || erpFeatureLabels.length > 0;
  const showIdCard = idCardTotal > 0 || perId > 0;
  const showOther = serviceRows.length > 0 || digitalRowsWithMeta.length > 0;

  return (
    <Box>
      {showErp && (
        <Section title="ERP">
          {erpFeatureLabels.length > 0 ? (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Modules checked
              </Typography>
              {erpFeatureLabels.map((feature, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 0.25 }}>
                  • {feature}
                </Typography>
              ))}
            </Box>
          ) : null}

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Calculation
          </Typography>
          <Table size="small" sx={{ mb: 1.5 }}>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Formula</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>ERP licence (before GST)</TableCell>
                <TableCell>
                  {d.amountEntry === "Number of students"
                    ? `${fmtMoney(perStudent)} × ${students} students`
                    : `${fmtMoney(perStudent)} (flat up to ${students || "—"} students)`}
                </TableCell>
                <TableCell align="right">{fmtMoney(erpBase)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>GST</TableCell>
                <TableCell>{`${gstPct}% of ${fmtMoney(erpBase)}`}</TableCell>
                <TableCell align="right">{fmtMoney(erpGst)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <b>ERP total (incl. GST)</b>
                </TableCell>
                <TableCell />
                <TableCell align="right">
                  <b>{fmtMoney(erpTotal)}</b>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {paymentTerms.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Payment terms (ERP only)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Term</TableCell>
                    <TableCell>Date</TableCell>
                    {paymentEditBy === "percentage" ? <TableCell align="right">%</TableCell> : null}
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentTerms.map((t, i) => {
                    const pct = safeNumber(t?.payment);
                    const amount =
                      paymentEditBy === "amount" ? safeNumber(t?.amount) : (pct / 100) * erpTotal;
                    return (
                      <TableRow key={`term-${i}`}>
                        <TableCell>{`Term ${i + 1}`}</TableCell>
                        <TableCell>{t?.date || "-"}</TableCell>
                        {paymentEditBy === "percentage" ? (
                          <TableCell align="right">{(pct || 0).toFixed(2)}</TableCell>
                        ) : null}
                        <TableCell align="right">{fmtMoney(amount)}</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={paymentEditBy === "percentage" ? 3 : 2} align="right">
                      <b>Total</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{fmtMoney(erpTotal)}</b>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          )}
        </Section>
      )}

      {showIdCard && (
        <Section title="ID card">
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Calculation
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Formula</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>ID cards</TableCell>
                <TableCell>{`${fmtMoney(perId)} × ${students || 0} students`}</TableCell>
                <TableCell align="right">{fmtMoney(idCardTotal)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} align="right">
                  <b>ID card total</b>
                </TableCell>
                <TableCell align="right">
                  <b>{fmtMoney(idCardTotal)}</b>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Section>
      )}

      {showOther && (
        <Section title="Other services">
          {serviceRows.length > 0 ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Add-on services (incl. GST)
              </Typography>
              <Table size="small" sx={{ mb: 1.5 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell>Calculation</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {serviceRows.map((r) => (
                    <TableRow key={r.name}>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>
                        Base {fmtMoney(r.amount)}
                        {r.gstPct ? ` + GST ${r.gstPct}% (${fmtMoney(r.gstAmt)})` : ""}
                        {r.description ? ` — ${r.description}` : ""}
                      </TableCell>
                      <TableCell align="right">{fmtMoney(r.total)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2} align="right">
                      <b>Add-on subtotal</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{fmtMoney(servicesTotal)}</b>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : null}

          {digitalRowsWithMeta.length > 0 ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Other service items (billing cycle calculation)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell sx={{ minWidth: 220, maxWidth: 420 }}>Calculation</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {digitalRowsWithMeta.map((row) => {
                    const rawMeta = d?.digitalfeature?.[row.id];
                    const calc = singleDigitalFeatureAmount(rawMeta, d);
                    return (
                      <TableRow key={row.id}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            verticalAlign: "top",
                          }}
                        >
                          {calc.note || "-"}
                        </TableCell>
                        <TableCell align="right">{fmtMoney(calc.amount)}</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={2} align="right">
                      <b>Other services subtotal</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{fmtMoney(digitalTotal)}</b>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No other service items selected.
            </Typography>
          )}
        </Section>
      )}

      <Section title="Grand total (all services)">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <LabelValue label="ERP (incl. GST)" value={fmtMoney(erpTotal)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LabelValue label="ID card" value={fmtMoney(idCardTotal)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LabelValue label="Add-ons" value={fmtMoney(servicesTotal)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LabelValue label="Other services" value={fmtMoney(digitalTotal)} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={800}>
              Grand total: {fmtMoney(grandTotal)}
            </Typography>
          </Grid>
        </Grid>
      </Section>

      <Section title="Uploaded Agreement Documents">
        {(() => {
          const documents = Array.isArray(d.agreement_documents) ? d.agreement_documents : [];
          const hasBackwardCompatible = !!d.agreement_form_details?.file;
          
          if (documents.length === 0 && !hasBackwardCompatible) {
            return (
              <Typography variant="body2" color="textSecondary">
                No documents uploaded
              </Typography>
            );
          }
          
          // Combine documents list with backward-compatible single document
          const allDocuments = [...documents];
          if (hasBackwardCompatible && !documents.find(doc => doc.file === d.agreement_form_details.file)) {
            allDocuments.push(d.agreement_form_details);
          }
          
          return (
            <Box>
              {allDocuments.map((doc, index) => {
                const formatDate = (dateString) => {
                  if (!dateString) return '';
                  try {
                    const date = new Date(dateString);
                    return date.toLocaleDateString('en-IN', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  } catch {
                    return dateString;
                  }
                };
                
                return (
                  <Box key={index} mb={1} p={1} border={1} borderColor="grey.300" borderRadius={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight="medium">
                        Document {index + 1} {doc.file_name ? `- ${doc.file_name}` : ''}
                      </Typography>
                      {doc.file && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Download />}
                          component="a"
                          href={doc.file}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </Button>
                      )}
                    </Box>
                    {doc.created && (
                      <Typography variant="caption" color="textSecondary">
                        Uploaded on: {formatDate(doc.created)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        })()}
      </Section>
    </Box>
  );
}


